import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../ThemeContext';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import api from '../api';

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0, index = 0, onClick, isSelected, ...props }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  // Determine border color: selected state takes priority over hover
  const getBorderColor = () => {
    if (isSelected) return '#34c759';
    if (isHovered) return hexToRgba(hoverColor, 0.4);
    return isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)';
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      whileTap={onClick ? { scale: 0.98 } : {}}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={onClick}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className} ${onClick ? 'cursor-pointer' : ''} ${isSelected ? 'border-[3px]' : ''}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: getBorderColor(),
        ...props.style,
      }}
      {...Object.fromEntries(Object.entries(props).filter(([key]) => key !== 'style'))}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
};

const plans = [
  { 
    months: 1, 
    title: 'Monthly Plan',
    fallbackMonthlyPrice: 29.99,
    totalBilling: null,
    savings: 'Solid Value!',
    description: 'This subscription auto-renews.',
    features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions']
  },
  { 
    months: 3, 
    title: 'Three-Month Plan',
    fallbackMonthlyPrice: 25.99,
    totalBilling: '$77.97 billed every 90 days',
    savings: 'A Great Deal!',
    description: 'This subscription auto-renews.',
    features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions']
  },
  { 
    months: 6, 
    title: 'Six-Month Plan',
    fallbackMonthlyPrice: 19.99,
    totalBilling: '$119.94 billed every 180 days',
    savings: '33% Savings!',
    description: 'This subscription auto-renews.',
    features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions']
  },
  { 
    months: 12, 
    title: 'Annual Plan',
    fallbackMonthlyPrice: 16.50,
    totalBilling: '$198.00 billed annually.',
    savings: 'Best Deal! 45% Savings!',
    description: 'This subscription does not auto-renew.',
    features: ['Full access to all features', 'Rank-Up Competitions', 'Global Leaderboard', 'Practice questions']
  },
];

const PricingPage = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const [selected, setSelected] = useState(1);
  const [prices, setPrices] = useState({});

  // Memoize title gradient
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  useEffect(() => {
    api.get('/api/stripe/prices/').then(res => setPrices(res.data || {})).catch(() => setPrices({}));
  }, []);

  const formatUnit = (p) => {
    if (!p) return '';
    const count = p.interval_count || 1;
    const interval = p.interval;
    if (interval === 'month') return count === 1 ? '/Month' : `/Month`;
    if (interval === 'year') return '/Month';
    if (interval === 'day') return count === 1 ? '/ day' : ` / ${count} days`;
    return '';
  };

  const computePriceLabel = (months) => {
    const p = prices[months];
    const plan = plans.find(plan => plan.months === months);
    const fallbackMonthlyPrice = Number(plan?.fallbackMonthlyPrice);
    const hasStripePrice = p?.unit_amount !== null && p?.unit_amount !== undefined && Number.isFinite(Number(p.unit_amount));
    const base = hasStripePrice ? Number(p.unit_amount) / 100 : fallbackMonthlyPrice * months;
    const unit = p ? formatUnit(p) || '/Month' : '/Month';

    if (!Number.isFinite(base)) {
      return {
        price: '0.00',
        originalPrice: null,
        unit: '/Month',
        hasDiscount: false,
        discountText: null
      };
    }

    return {
      price: base.toFixed(2),
      originalPrice: null,
      unit: unit,
      hasDiscount: false,
      discountText: null
    };
  };

  const computeTotalBilling = (months) => {
    const priceInfo = computePriceLabel(months);
    if (!priceInfo) return null;
    
    const discountedTotal = parseFloat(priceInfo.price);
    
    let billingText = '';
    if (months === 3) {
      billingText = `$${discountedTotal.toFixed(2)} billed every 90-days`;
    } else if (months === 6) {
      billingText = `$${discountedTotal.toFixed(2)} billed every 180 days`;
    } else if (months === 12) {
      billingText = `$${discountedTotal.toFixed(2)} billed annually.`;
    }
    else {
      return null;
    }
    
    return billingText;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center font-zonaProRegular p-4 pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-6 sm:mb-8 mt-16 sm:mt-16 md:mt-16"
        >
          <h1
            key={`pricing-title-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-zonaProUltra mb-3 sm:mb-4 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Game Your SAT
          </h1>
          <h1
            key={`pricing-subtitle-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-zonaProUltra mb-3 sm:mb-4 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Your Level-Up Starts Now
          </h1>
        </motion.div>

        {/* Main content above pricing cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mb-8 md:mb-12 max-w-4xl mx-auto"
        >
          <h2
            key={`pricing-h2-${isDarkMode}`}
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Authentic practice on your schedule.
          </h2>
          <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 mb-6 font-zonaProRegular">
            Game Your SAT features flexible pricing, so you can practice at your own pace. Invite friends to play competitively — and earn points!
          </p>
          <div className="mb-6">
            <h3
              key={`free-trial-h3-${isDarkMode}`}
              className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra mb-3 relative inline-block"
              style={{
                background: titleGradient,
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'shine 10s linear infinite',
                lineHeight: '1.2',
                paddingBottom: '0.25rem'
              }}
            >
              Free for 7 days!
            </h3>
            <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular">
              Start your 7-day free trial of Game Your SAT. Explore everything, see your progress, and pick the plan that works for you. Your chosen subscription starts automatically after the trial.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-8xl mx-auto mb-12">
          {plans.map((p, idx) => {
            const priceInfo = computePriceLabel(p.months);
            const isHighlighted = selected === p.months;
            return (
              <motion.div
                key={p.months}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(p.months);
                }}
                className="cursor-pointer"
              >
                <Card
                  delay={0}
                  isSelected={isHighlighted}
                  className={`h-full flex flex-col relative ${isHighlighted ? 'ring-4 ring-[#34c759] bg-[#34c759]/5 dark:bg-[#34c759]/10' : ''}`}
                  onClick={undefined}
                >
                  {/* Selected Indicator - Green Checkmark in Top Right */}
                  {isHighlighted && (
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3, type: "spring" }}
                      className="absolute top-3 right-3 z-10"
                    >
                      <CheckCircle2 
                        size={28} 
                        className="text-[#34c759]" 
                        fill="#34c759"
                        stroke="white"
                        strokeWidth={2}
                      />
                    </motion.div>
                  )}
                  <div className="flex flex-col h-full">
                    {/* Plan Name */}
                    <h3
                      key={`plan-title-${p.months}-${isDarkMode}`}
                      className="text-xl md:text-2xl font-bold font-zonaProUltra mb-3 relative inline-block"
                      style={{
                        background: titleGradient,
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'shine 10s linear infinite',
                        lineHeight: '1.2',
                        paddingBottom: '0.25rem'
                      }}
                    >
                      {p.title}
                    </h3>

                    {/* Price */}
                    <div className="mb-3">
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-900 dark:text-gray-100">
                          ${(parseFloat(priceInfo.price) / p.months).toFixed(2)}
                        </span>
                        <span className="text-lg md:text-xl font-zonaProRegular text-gray-600 dark:text-gray-400">
                          {priceInfo.unit}
                        </span>
                      </div>
                      {/* Total Billing Info */}
                      {(() => {
                        const computedTotalBilling = computeTotalBilling(p.months);
                        const displayBilling = computedTotalBilling !== null ? computedTotalBilling : p.totalBilling;
                        return displayBilling !== null ? (
                          <p className="text-sm md:text-base font-zonaProRegular text-gray-500 dark:text-gray-400 mt-1">
                            {displayBilling}
                          </p>
                        ) : (
                            <p className="text-sm md:text-base font-zonaProRegular text-white dark:text-black mt-1">
                           .
                      </p>
                        );
                      })()}
                    </div>

                    {/* Savings Badge */}
                    {p.savings !== null ? (
                      <div className="mb-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm md:text-base font-bold font-zonaProUltra ${
                          p.savings.includes('Best Deal') 
                            ? 'bg-[#06bae4] text-white dark:bg-[#06bae4] dark:text-white'
                            : 'bg-[#34c759] text-white dark:bg-[#34c759] dark:text-white'
                        }`}>
                          {p.savings}
                        </span>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <span className="text-sm md:text-base font-bold font-zonaProUltra text-white dark:text-black">
                          .
                        </span>
                      </div>
                    )}

                    {/* Description */}
                    <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 font-zonaProRegular mb-4">
                      {p.description}
                    </p>

                  {/* Cancel Anytime */}
                  <Link
                    to="/terms"
                    className="text-base italic underline text-gray-600 dark:text-gray-400 hover:text-[#06bae4] dark:hover:text-[#06bae4] transition-colors font-zonaProRegular"
                  >
                    Cancel anytime
                  </Link>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Sign Up Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="text-center"
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/signup"
                className="inline-block px-8 py-4 bg-[#212D82] dark:bg-[#ff7a00] hover:bg-[#1a2469] dark:hover:bg-[#e66900] text-white rounded-full font-bold font-zonaProUltra text-lg md:text-xl transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Level Up Now
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
